import Controller from '@ember/controller';
import type StoreService from 'scaleflow/services/store';
import type GoalModel from 'scaleflow/models/goal';
import type OrganisationModel from 'scaleflow/models/organisation';
import type InitiativeModel from 'scaleflow/models/initiative';
import type BetaToggleService from 'scaleflow/services/beta-toggle';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import capitalize from 'scaleflow/utils/capitalize';
import type RoadmapService from 'scaleflow/services/roadmap';
import type IntlService from 'ember-intl/services/intl';
import type Delta from 'quill-delta';
import type OrganisationService from 'scaleflow/services/organisation';

type FieldOption = {
  label: string;
  value: string;
  checked: boolean;
};

export default class CompanyController extends Controller {
  @service declare store: StoreService;
  @service declare roadmap: RoadmapService;
  @service declare intl: IntlService;
  @service declare organisation: OrganisationService;
  @service declare betaToggle: BetaToggleService;

  get _organisation(): OrganisationModel | null {
    return this.organisation.organisation as OrganisationModel | null;
  }

  queryParams = [
    'column',
    'row',
    'effort',
    'uncertainties',
    'waiting',
    'updatedAt',
    'tickets',
    'hiddenRows',
    'hiddenColumns',
  ];

  @tracked column = 'source';
  @tracked row = 'status';
  @tracked effort = true;
  @tracked uncertainties = true;
  @tracked tfos = true;
  @tracked waiting = true;
  @tracked updatedAt = true;
  @tracked tickets = true;
  @tracked slideoverIsOpen = false;
  @tracked hiddenRows = JSON.stringify({
    Parked: true,
    Archived: true,
    Draft: true,
  });
  @tracked hiddenColumns = '';

  get topLevelGoals() {
    const goals = this.store.peekAll('goal') as unknown as GoalModel[];
    const parent_goals = goals.filter((g: GoalModel) => !g.parent.content);
    return parent_goals.slice(0, 5);
  }

  get topLevelGoalMetrics() {
    const seen = new Set<string>();
    const metrics: any[] = [];
    this.topLevelGoals.forEach((goal: GoalModel) => {
      const targets = goal.targets.content ?? [];
      targets.forEach((target: any) => {
        const metric = target.metric?.content;
        if (metric?.id && !seen.has(metric.id as string)) {
          seen.add(metric.id as string);
          metrics.push(metric);
        }
      });
    });
    return metrics;
  }

  get activeFieldsMap() {
    return {
      effort: this.effort,
      uncertainties: this.uncertainties,
      tfos: this.tfos,
      waiting: this.waiting,
      updatedAt: this.updatedAt,
      tickets: this.tickets,
    };
  }

  get fieldOptions(): FieldOption[] {
    return [
      {
        label: this.intl.t('dictionary.effort'),
        value: 'effort',
        checked: this.effort,
      },
      {
        label: this.intl.t('dictionary.uncertainty', { num: 2 }),
        value: 'uncertainties',
        checked: this.uncertainties,
      },
      {
        label: this.intl.t('dictionary.waiting-for-answers'),
        value: 'waiting',
        checked: this.waiting,
      },
      {
        label: this.intl.t('dictionary.last-updated'),
        value: 'updatedAt',
        checked: this.updatedAt,
      },
      {
        label: this.intl.t('_initiatives.tickets'),
        value: 'tickets',
        checked: this.tickets,
      },
    ];
  }

  get activeFields() {
    return this.fieldOptions.reduce((acc, field) => {
      if (field.checked === true) acc.push(field);
      return acc;
    }, [] as FieldOption[]);
  }

  get activeFieldsKeys() {
    return this.activeFields.reduce((acc, item) => {
      acc.push(item.value);
      return acc;
    }, [] as string[]);
  }

  @action
  setColumn(e: { value: string }) {
    this.column = e.value;
  }

  @action
  setRow(e: { value: string }) {
    this.row = e.value;
  }

  get options() {
    return [
      {
        label: capitalize(
          this.intl.t('_initiatives.source', {
            num: 1,
          }),
        ),
        value: 'source',
      },
      {
        label: capitalize(this.intl.t('_initiatives.status')),
        value: 'status',
      },
      {
        label: capitalize(
          this.intl.t('_initiatives.teams', {
            num: 1,
          }),
        ),
        value: 'teams',
      },
      {
        label: capitalize(
          this.intl.t('_initiatives.themes', {
            num: 1,
          }),
        ),
        value: 'theme',
      },
      {
        label: capitalize(this.intl.t('_initiatives.progress')),
        value: 'progress',
      },
      {
        label: capitalize(
          this.intl.t('dictionary.goals', {
            num: 1,
          }),
        ),
        value: 'goals',
      },
    ];
  }

  @action
  onFieldSelect(fields: string[]) {
    this.fieldOptions.forEach((item) => {
      const key = item.value as keyof typeof this.activeFieldsMap;
      if (fields.includes(item.value)) this[key] = true;
      else this[key] = false;
    });
  }

  @action
  updateRowFilters(hiddenRows: string) {
    this.hiddenRows = JSON.stringify(hiddenRows);
  }

  @action
  updateColumnFilters(hiddenColumns: string) {
    this.hiddenColumns = JSON.stringify(hiddenColumns);
  }

  get initiatives() {
    return this.store.peekAll('initiative');
  }

  @action
  setMissionDelta(e: Delta) {
    if (this._organisation) this._organisation.mission = e;
  }

  @action
  setVisionDelta(e: Delta) {
    if (this._organisation) this._organisation.vision = e;
  }

  @action
  saveOrganisation() {
    if (!this._organisation) return;
    if (
      Object.values((this._organisation as any).changedAttributes() as object)
        .length === 0
    )
      return;
    void (this._organisation as any).save();
  }

  getInitiativeSortPriority(initiative: InitiativeModel): number {
    // Completed last (priority 3)
    if (initiative.status === 'COMPLETED') return 3;
    // // Blocked next (priority 2)
    // if (
    //   initiative.openWfas.length ||
    //   initiative.unarchivedUncertaintiesDetailed.open
    // )
    //   return 2;
    // In progress first (priority 1)
    if (initiative.status === 'ACTIVE') return 1;
    // Everything else (priority 0)
    return 0;
  }

  groupInitiativesByTheme(initiatives: InitiativeModel[]): Array<{
    theme: any;
    initiatives: InitiativeModel[];
    goals: GoalModel[];
  }> {
    const grouped = new Map();
    const other: {
      theme: null;
      initiatives: InitiativeModel[];
      goals: GoalModel[];
    } = {
      theme: null,
      initiatives: [],
      goals: [],
    };

    initiatives.forEach((initiative: InitiativeModel) => {
      const theme = initiative.theme?.content;
      const initiativeGoals = initiative.goals?.content || [];

      if (!theme) {
        other.initiatives.push(initiative);
        initiativeGoals.forEach((goal: GoalModel) => {
          if (!other.goals.find((g) => (g as any).id === (goal as any).id)) {
            other.goals.push(goal);
          }
        });
      } else {
        if (!grouped.has(theme.id)) {
          grouped.set(theme.id, { theme, initiatives: [], goals: [] });
        }
        const group = grouped.get(theme.id);
        group.initiatives.push(initiative);
        initiativeGoals.forEach((goal: GoalModel) => {
          if (
            !group.goals.find(
              (g: GoalModel) => (g as any).id === (goal as any).id,
            )
          ) {
            group.goals.push(goal);
          }
        });
      }
    });

    // Sort initiatives in each group
    grouped.forEach((group) => {
      group.initiatives.sort(
        (a: InitiativeModel, b: InitiativeModel) =>
          this.getInitiativeSortPriority(a) - this.getInitiativeSortPriority(b),
      );
    });

    // Sort initiatives in other group
    other.initiatives.sort(
      (a: InitiativeModel, b: InitiativeModel) =>
        this.getInitiativeSortPriority(a) - this.getInitiativeSortPriority(b),
    );

    const result = Array.from(grouped.values());

    if (other.initiatives.length > 0) {
      result.push(other);
    }
    return result;
  }

  get nowInitiatives(): InitiativeModel[] {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return this.store
      .peekAll<InitiativeModel>('initiative')
      .filter((i: InitiativeModel) => {
        if (i.status === 'ACTIVE') return true;
        if (i.status === 'COMPLETED') {
          const updatedAt = new Date(i.updated_at);
          return updatedAt >= threeMonthsAgo;
        }
        return false;
      });
  }

  get nowInitiativesGroupedByTheme() {
    return this.groupInitiativesByTheme(this.nowInitiatives);
  }

  get nextInitiatives(): InitiativeModel[] {
    return this.store
      .peekAll<InitiativeModel>('initiative')
      .filter(
        (i: InitiativeModel) =>
          i.status === 'READY FOR PLANNING' || i.status === 'READY',
      );
  }

  get nextInitiativesGroupedByTheme() {
    return this.groupInitiativesByTheme(this.nextInitiatives);
  }

  get laterInitiatives(): InitiativeModel[] {
    return this.store
      .peekAll<InitiativeModel>('initiative')
      .filter((i: InitiativeModel) => i.status === 'DISCOVERY');
  }

  get laterInitiativesGroupedByTheme() {
    return this.groupInitiativesByTheme(this.laterInitiatives);
  }
}
